import { FileText, Zap, ArrowRight } from 'lucide-react';
import { Button } from '@powercore/ui';

interface WelcomeHeroProps {
  onConnectSources: () => void;
  onAddManualNote: () => void;
  onSkip?: () => void;
}

export function WelcomeHero({ onConnectSources, onAddManualNote, onSkip }: WelcomeHeroProps) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-mazo-100 to-mazo-50 border border-mazo-200">
            <FileText className="w-8 h-8 text-mazo-500" />
          </div>
          <ArrowRight className="w-8 h-8 text-gray-300" />
          <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-mazo-400 to-mazo-600 shadow-lg">
            <Zap className="w-8 h-8 text-white" />
          </div>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Generate your first successful LinkedIn posts
        </h1>
        <p className="text-lg text-gray-600 mb-8">
          Transform your meeting notes and ideas into engaging content that builds your professional brand
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-4">
          <Button variant="primary" size="lg" onClick={onConnectSources} className="w-full sm:w-auto">
            Sync your meeting notes
          </Button>
          <Button variant="outline" size="lg" onClick={onAddManualNote} className="w-full sm:w-auto">
            Add notes manually
          </Button>
        </div>
        {onSkip && (
          <button onClick={onSkip} className="text-sm text-gray-500 hover:text-gray-700 transition-colors">
            Add sources later
          </button>
        )}
      </div>
      <div className="w-full max-w-3xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <BenefitCard iconType="target" title="Authentic voice" description="Posts that sound like you, not a bot" bgColor="bg-amber-50" />
          <BenefitCard iconType="lightning" title="Fast creation" description="From meeting notes to posts in seconds" bgColor="bg-mazo-50" />
          <BenefitCard iconType="chart" title="Consistent presence" description="Never run out of content ideas" bgColor="bg-mazo-50" />
        </div>
      </div>
    </div>
  );
}

interface BenefitCardProps {
  iconType: 'target' | 'lightning' | 'chart';
  title: string;
  description: string;
  bgColor: string;
}

function BenefitCard({ iconType, title, description, bgColor }: BenefitCardProps) {
  const emojis: Record<string, string> = {
    target: '\u{1F3AF}',
    lightning: '\u26A1',
    chart: '\u{1F4C8}'
  };
  return (
    <div className="flex flex-col items-center text-center p-6 rounded-xl bg-white border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all">
      <div className={`flex items-center justify-center w-12 h-12 rounded-full ${bgColor} mb-4`}>
        <span className="text-2xl" role="img" aria-label={iconType}>{emojis[iconType]}</span>
      </div>
      <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  );
}

export default WelcomeHero;
